import React from 'react';
import { FormattedMessage as T } from '@/components/FormattedMessage';
import { Classes, Icon, IconSize, H4, Button } from '@blueprintjs/core';

import withDrawerActions from '@/containers/Drawer/withDrawerActions';
import { useDrawerContext } from './DrawerProvider';
import { useInlineDrawer } from './InlineDrawerContext';

import { compose } from '@/utils';
import styled from 'styled-components';

/**
 * Drawer header content.
 */
interface DrawerHeaderContentRootProps {
  icon?: any;
  title?: React.ReactNode;
  subTitle?: React.ReactNode;
  /** When provided, renders a back arrow button at the left of the title. */
  onBack?: () => void;
  closeDrawer: (name: string) => void;
}

function DrawerHeaderContentRoot(props: DrawerHeaderContentRootProps) {
  const {
    icon,
    title = <T id={'view_paper'} />,
    subTitle,
    onBack,
    closeDrawer,
  } = props;
  const { name } = useDrawerContext();
  const { inline, setTitle } = useInlineDrawer();

  // Inline (docked side-panel) mode: the host panel owns the header, so this
  // per-drawer title bar is hidden and its title is surfaced to the panel.
  React.useEffect(() => {
    if (inline && setTitle) setTitle(title);
  }, [inline, setTitle, title]);
  if (inline) {
    return null;
  }

  if (title == null) {
    return null;
  }
  const handleClose = (event: React.MouseEvent) => {
    closeDrawer(name);
  };

  return (
    <div className={Classes.DRAWER_HEADER}>
      {onBack && (
        <Button
          aria-label="Back"
          icon={<Icon icon="arrow-left" iconSize={IconSize.LARGE} />}
          minimal={true}
          onClick={() => onBack()}
          style={{ marginRight: 4 }}
        />
      )}
      {icon && <Icon icon={icon} iconSize={IconSize.STANDARD} />}
      <H4>
        {title}
        <SubTitle>{subTitle}</SubTitle>
      </H4>

      <Button
        aria-label="Close"
        className={Classes.DIALOG_CLOSE_BUTTON}
        icon={<Icon icon="small-cross" iconSize={IconSize.LARGE} />}
        minimal={true}
        onClick={handleClose}
      />
    </div>
  );
}

export const DrawerHeaderContent = compose(withDrawerActions)(
  DrawerHeaderContentRoot,
);

/**
 * SubTitle Drawer header.
 * @returns {React.JSX}
 */
function SubTitle({ children }: { children?: React.ReactNode }) {
  if (children == null) {
    return null;
  }

  return <SubTitleHead>{children}</SubTitleHead>;
}

const SubTitleHead = styled.div`
  --x-color-text: var(--color-muted-text, #94a3b8);

  .bp4-dark & {
    --x-color-text: rgba(255, 255, 255, 0.5);
  }
  color: var(--x-color-text);
  font-size: 11px;
  font-weight: 500;
  line-height: 1.2;
  padding: 3px 0 0;
  margin: 0;
`;
